/**
 * visitTypeNoteConfigStore (demo-only)
 * ──────────────────────────────────────────────────────────────────────
 * Per-provider, per-visit-type note configuration. Backed by
 * `localStorage` (key: `DEMO_VISIT_TYPE_NOTE_CONFIG_v1`) so nothing
 * persists to a server. Entire module is a no-op outside demo mode.
 *
 * Data model:
 *   {
 *     [visitTypeId]: {
 *       narrativeStructure: 'consult' | 'soap' | 'hp',
 *       sections: [
 *         { id: 'chiefComplaint', label: 'Chief Complaint', enabled: true, order: 0 },
 *         ...
 *       ],
 *       copyForward: {
 *         // Section-level on/off
 *         sectionsEnabled: { hpi: true, medications: true, ... },
 *         // Field-level rule per section:
 *         //   'always'        — always copy from last note
 *         //   'same-visit'    — copy only if the previous encounter had
 *         //                     the same visit type
 *         //   'never'         — never copy
 *         rules: { hpi: 'same-visit', medications: 'always', ... },
 *       },
 *       assessments: {
 *         // Seeded at encounter-open; provider can add/remove at write
 *         // time in the note composer.
 *         seeds: [
 *           { id: 'a-1', label: 'A/P — Primary' },
 *           ...
 *         ],
 *       },
 *     }
 *   }
 *
 * NOT wired to any backend. Not reachable in real mode (callers MUST
 * gate with `isDemo` before invoking these APIs).
 */

const KEY = 'DEMO_VISIT_TYPE_NOTE_CONFIG_v1';

const DEFAULT_CONSULT_SECTIONS = [
  { id: 'chiefComplaint',  label: 'Reason for Consultation',  enabled: true,  order: 0 },
  { id: 'referringSource', label: 'Referring Source',         enabled: true,  order: 1 },
  { id: 'hpi',             label: 'History of Present Illness', enabled: true, order: 2 },
  { id: 'pastMedHx',       label: 'Past Medical History',     enabled: true,  order: 3 },
  { id: 'medications',     label: 'Medications',              enabled: true,  order: 4 },
  { id: 'allergies',       label: 'Allergies',                enabled: true,  order: 5 },
  { id: 'socialHx',        label: 'Social History',           enabled: true,  order: 6 },
  { id: 'familyHx',        label: 'Family History',           enabled: false, order: 7 },
  { id: 'ros',             label: 'Review of Systems',        enabled: true,  order: 8 },
  { id: 'vitals',          label: 'Vitals',                   enabled: true,  order: 9 },
  { id: 'physical',        label: 'Physical Examination',     enabled: true,  order: 10 },
  { id: 'labsImaging',     label: 'Labs / Imaging Reviewed',  enabled: true,  order: 11 },
  { id: 'assessment',      label: 'Impression',               enabled: true,  order: 12 },
  { id: 'plan',            label: 'Recommendations / Plan',   enabled: true,  order: 13 },
];

const DEFAULT_SOAP_SECTIONS = [
  { id: 'subjective', label: 'Subjective', enabled: true, order: 0 },
  { id: 'objective',  label: 'Objective',  enabled: true, order: 1 },
  { id: 'assessment', label: 'Assessment', enabled: true, order: 2 },
  { id: 'plan',       label: 'Plan',       enabled: true, order: 3 },
];

const DEFAULT_HP_SECTIONS = [
  { id: 'chiefComplaint', label: 'Chief Complaint',    enabled: true, order: 0 },
  { id: 'hpi',            label: 'HPI',                 enabled: true, order: 1 },
  { id: 'pastMedHx',      label: 'Past Medical History',enabled: true, order: 2 },
  { id: 'medications',    label: 'Medications',         enabled: true, order: 3 },
  { id: 'allergies',      label: 'Allergies',           enabled: true, order: 4 },
  { id: 'socialHx',       label: 'Social History',      enabled: true, order: 5 },
  { id: 'familyHx',       label: 'Family History',      enabled: true, order: 6 },
  { id: 'ros',            label: 'ROS',                 enabled: true, order: 7 },
  { id: 'vitals',         label: 'Vitals',              enabled: true, order: 8 },
  { id: 'physical',       label: 'Physical Exam',       enabled: true, order: 9 },
  { id: 'assessment',     label: 'Assessment',          enabled: true, order: 10 },
  { id: 'plan',           label: 'Plan',                enabled: true, order: 11 },
];

const SECTIONS_BY_STRUCTURE = {
  consult: DEFAULT_CONSULT_SECTIONS,
  soap:    DEFAULT_SOAP_SECTIONS,
  hp:      DEFAULT_HP_SECTIONS,
};

export const NARRATIVE_STRUCTURES = [
  { id: 'consult', label: 'Consult Report',      desc: 'Referral-driven impression + recommendations' },
  { id: 'soap',    label: 'SOAP Note',           desc: 'Subjective / Objective / Assessment / Plan' },
  { id: 'hp',      label: 'History & Physical',  desc: 'Full admission H&P structure' },
];

export const COPY_FORWARD_RULES = [
  { id: 'always',     label: 'Always copy from last note' },
  { id: 'same-visit', label: 'Only if same visit type' },
  { id: 'never',      label: 'Never copy' },
];

const defaultConfig = (structure = 'consult') => {
  const sections = SECTIONS_BY_STRUCTURE[structure].map((s) => ({ ...s }));
  const sectionsEnabled = {};
  const rules = {};
  for (const s of sections) {
    sectionsEnabled[s.id] = s.enabled;
    rules[s.id] = s.id === 'chiefComplaint' ? 'never' : 'same-visit';
  }
  return {
    narrativeStructure: structure,
    sections,
    copyForward: { sectionsEnabled, rules },
    assessments: { seeds: [{ id: 'a-1', label: 'A/P — Primary' }] },
  };
};

const safeStorage = () => {
  try { return window.localStorage; } catch { return null; }
};

const readAll = () => {
  const ls = safeStorage();
  if (!ls) return {};
  try {
    const raw = ls.getItem(KEY);
    return raw ? JSON.parse(raw) : {};
  } catch { return {}; }
};

const writeAll = (data) => {
  const ls = safeStorage();
  if (!ls) return;
  try { ls.setItem(KEY, JSON.stringify(data)); } catch { /* quota */ }
};

export const getVisitTypeNoteConfig = (visitTypeId, structureHint = 'consult') => {
  const all = readAll();
  if (all[visitTypeId]) return all[visitTypeId];
  return defaultConfig(structureHint);
};

export const saveVisitTypeNoteConfig = (visitTypeId, config) => {
  const all = readAll();
  all[visitTypeId] = config;
  writeAll(all);
};

export const resetVisitTypeNoteConfig = (visitTypeId, structureHint = 'consult') => {
  const all = readAll();
  delete all[visitTypeId];
  writeAll(all);
  return defaultConfig(structureHint);
};

/**
 * Returns the default section list for a given narrative structure, in
 * case a caller needs to re-seed (e.g. user changes structure mid-edit).
 */
export const sectionsForStructure = (structure) =>
  (SECTIONS_BY_STRUCTURE[structure] || SECTIONS_BY_STRUCTURE.consult).map((s) => ({ ...s }));

/**
 * Assessment seed helpers — primarily for cardiology-style workflows
 * where the template seeds N default A/P blocks and the provider can
 * add/remove at note-writing time (Option B).
 */
export const addAssessmentSeed = (seeds, label = 'A/P — New block') => [
  ...seeds,
  { id: `a-${Date.now()}`, label },
];
export const removeAssessmentSeed = (seeds, id) => seeds.filter((s) => s.id !== id);
export const renameAssessmentSeed = (seeds, id, label) =>
  seeds.map((s) => (s.id === id ? { ...s, label } : s));
